import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { useContext, useEffect, useState } from "react";
import { log } from "../monitoring/log";
import { addTnplProductToCart } from "./add-product";
import { TryFirstContext } from "./context";

// Constants for localStorage and query parameter
const TRY_FIRST_PREVIEW_KEY = "redo.tryFirstPreview";
const TRY_FIRST_PREVIEW_PARAM = "tryFirstPreview";

// Custom hook to manage try-first preview mode
function useTryFirstPreview(): boolean {
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(false);

  useEffect(() => {
    // Check URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hasPreviewParam = urlParams.get(TRY_FIRST_PREVIEW_PARAM) === "true";

    // Store in localStorage if query param is present
    if (hasPreviewParam) {
      localStorage.setItem(TRY_FIRST_PREVIEW_KEY, "true");
    }

    // Check if preview is enabled from either source
    const isEnabled =
      hasPreviewParam || localStorage.getItem(TRY_FIRST_PREVIEW_KEY) === "true";
    setIsPreviewEnabled(isEnabled);
  }, []);

  return isPreviewEnabled;
}

export function TryFirstButton() {
  const context = useContext(TryFirstContext);
  const [isLoading, setIsLoading] = useState(false);
  const isPreviewMode = useTryFirstPreview();

  if (!context) {
    throw new Error("TryFirstButton must be used within a TryFirstProvider");
  }

  // Only render the button when state is READY
  if (context.state !== "READY") {
    return null;
  }

  // Check if widget is enabled OR if preview mode is active
  if (!context.autocheck.tryFirst?.widget?.enabled && !isPreviewMode) {
    return null;
  }

  const handleTryFirstClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const { productForm, sellingPlanId, autocheck } = context;

    setIsLoading(true);

    try {
      await addTnplProductToCart({ autocheck, productForm, sellingPlanId });
    } catch (error) {
      console.error("Try First: Error adding to cart", error);
      log("Try First: Failed to add to cart", { error });
    } finally {
      setIsLoading(false);
    }
  };

  // Get subtitle text from autocheck settings, with fallback
  const rawSubtitleText =
    context.autocheck.tryFirst?.widget?.subtitleText ||
    "Try at home for %trialLength% days. Only pay for what you keep.";

  // Get trial period from the template
  const trialPeriodDays = context.autocheck.tryFirst?.template?.trialPeriodDays;

  // Replace %trialLength% placeholder only if trial period exists
  const subtitleText = trialPeriodDays
    ? rawSubtitleText.replace(/%trialLength%/g, `${trialPeriodDays}`)
    : rawSubtitleText;

  return (
    <div data-target="try-first-button-container">
      <style dangerouslySetInnerHTML={{ __html: genTryFirstCss() }} />
      <div data-target="try-first-button-and-text">
        <RedoButton
          disabled={isLoading}
          hierarchy="secondary"
          onClick={handleTryFirstClick}
          pending={isLoading}
          size="md"
          text="Try before you buy"
          theme="normal"
        />
        <div data-target="try-first-disclaimer">{subtitleText}</div>
      </div>
    </div>
  );
}

const genTryFirstCss = () => {
  // TODO: gen from Autocheck
  return `
    [data-target="try-first-button-container"] {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    [data-target="try-first-button-and-text"] {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    [data-target="try-first-disclaimer"] {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 12px;
      color: #aaaaaa;
    }
  `;
};
